import { snippet } from "@codemirror/autocomplete";
import { EditorSelection } from "@codemirror/state";
import { closeSearchPanel, openSearchPanel, searchPanelOpen } from "@codemirror/search";

import { toggleRanges, wrapRanges } from "./ranges.js";
import {
    toggleListForRanges,
    unwrapBulletList,
    unwrapDescriptionList,
    unwrapNumberedList,
    wrapInBulletList,
    wrapInDescriptionList,
    wrapInNumberedList,
    ancestorListType,
} from "./lists.js";
import { minimumListDepthForSelection } from "../utils/tree-operations/ancestors.js";
import { isVisual } from "../visual-state.js";
import { snippets } from "./snippets.js";

const dispatchSelectionChange = (view, changes, selection) => {
    view.dispatch({
        changes,
        selection,
        scrollIntoView: true,
    });
    return true;
};

const replaceSelection = (view, builder) => {
    const mainSelection = view.state.selection.main;
    const from = Math.min(mainSelection.from, mainSelection.to);
    const to = Math.max(mainSelection.from, mainSelection.to);
    const selectedText = view.state.sliceDoc(from, to);
    const next = builder({ from, to, selectedText, state: view.state });

    if (!next?.insert) {
        return false;
    }

    return dispatchSelectionChange(
        view,
        {
            from,
            to,
            insert: next.insert,
        },
        next.selection || { anchor: from + next.insert.length },
    );
};

export const ensureEmptyLine = (state, range, direction = "below") => {
    let pos = range.anchor;
    let suffix = "";
    let prefix = "";

    const line = state.doc.lineAt(pos);

    if (line.text.trim().length) {
        if (direction === "below") {
            pos = Math.min(line.to + 1, state.doc.length);
        } else {
            pos = Math.max(line.from - 1, 0);
        }

        const neighbouringLine = state.doc.lineAt(pos);
        if (neighbouringLine.length && direction === "below") {
            suffix = "\n";
        } else if (neighbouringLine.length && direction === "above") {
            prefix = "\n";
        }
    }

    return { pos, suffix, prefix };
};

export const toggleBold = toggleRanges("\\textbf");
export const toggleItalic = toggleRanges("\\textit");

export const wrapInHref = wrapRanges("\\href{}{", "}", false, (range, view) =>
    isVisual(view) ? range : EditorSelection.cursor(range.from - 2),
);

export const toggleBulletList = toggleListForRanges("itemize");
export const toggleNumberedList = toggleListForRanges("enumerate");

export const wrapInInlineMath = wrapRanges("\\(", "\\)");
export const wrapInDisplayMath = wrapRanges("\n\\[", "\\]\n");

export const insertFigure = (view) => {
    const { state, dispatch } = view;
    const { pos, suffix } = ensureEmptyLine(state, state.selection.main);
    const template = `\n${snippets.figure}\n${suffix}`;
    snippet(template)({ state, dispatch }, { label: "Figure" }, pos, pos);
    return true;
};

export const insertTable = (view, sizeX = 3, sizeY = 3) => {
    const { state, dispatch } = view;
    const visual = isVisual(view);
    const placeholder = visual ? "" : "#{}";
    const placeholderAtStart = visual ? "#{}" : "";
    const { pos, suffix } = ensureEmptyLine(state, state.selection.main);
    const template =
        `${placeholderAtStart}\n\\begin{table}\n` +
        `\t\\centering\n` +
        `\t\\begin{tabular}{${"c".repeat(sizeX)}}\n` +
        `${(`\t\t${placeholder} & ${placeholder}`.repeat(sizeX - 1) + "\\\\\n").repeat(sizeY)}` +
        `\t\\end{tabular}\n` +
        `\t\\caption{Caption}\n` +
        `\t\\label{tab:placeholder}\n` +
        `\\end{table}${suffix}`;

    snippet(template)({ state, dispatch }, { label: "Table" }, pos, pos);
    return true;
};

export const insertCite = (view) => {
    const { state, dispatch } = view;
    const pos = state.selection.main.anchor;
    snippet(snippets.cite)({ state, dispatch }, { label: "Cite" }, pos, pos);
    return true;
};

export const insertRef = (view) => {
    const { state, dispatch } = view;
    const pos = state.selection.main.anchor;
    snippet(snippets.ref)({ state, dispatch }, { label: "Ref" }, pos, pos);
    return true;
};

export const insertSymbol = (view, command = "\\alpha") =>
    replaceSelection(view, ({ from, selectedText }) => {
        const content = selectedText || command;
        return {
            insert: content,
            selection: {
                anchor: from,
                head: from + content.length,
            },
        };
    });

export const toggleSearch = (view) => {
    if (searchPanelOpen(view.state)) {
        closeSearchPanel(view);
    } else {
        openSearchPanel(view);
    }
    return true;
};

export const indentDecrease = (view) => {
    if (minimumListDepthForSelection(view.state) < 2) {
        return false;
    }

    switch (ancestorListType(view.state)) {
        case "itemize":
            return unwrapBulletList(view);
        case "enumerate":
            return unwrapNumberedList(view);
        case "description":
            return unwrapDescriptionList(view);
        default:
            return false;
    }
};

export const cursorIsAtStartOfListItem = (state) =>
    state.selection.ranges.every((range) => {
        const line = state.doc.lineAt(range.from);
        const prefix = state.sliceDoc(line.from, range.from);
        return /\\item\s*$/.test(prefix);
    });

export const indentIncrease = (view) => {
    if (minimumListDepthForSelection(view.state) < 1) {
        return false;
    }

    switch (ancestorListType(view.state)) {
        case "itemize":
            return wrapInBulletList(view);
        case "enumerate":
            return wrapInNumberedList(view);
        case "description":
            return wrapInDescriptionList(view);
        default:
            return false;
    }
};
